// ---------------------------------------------------------------------------
// InterAct Globally — light-weight i18n system
// EN is the source language and the fallback for any missing key.
// URL is the single source of truth for the active language:
//   /*         → en
//   /pt/*      → pt (Brazilian Portuguese)
//   /el/*      → el (Greek)
// ---------------------------------------------------------------------------
import { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import en from "@/i18n/locales/en.json";
import pt from "@/i18n/locales/pt.json";
import el from "@/i18n/locales/el.json";

export const LANGS = [
  { code: "en", label: "English", htmlLang: "en", ogLocale: "en_US" },
  { code: "pt", label: "Português (BR)", htmlLang: "pt-BR", ogLocale: "pt_BR" },
  { code: "el", label: "Ελληνικά", htmlLang: "el", ogLocale: "el_GR" },
];
export const LANG_CODES = LANGS.map((l) => l.code);
export const DEFAULT_LANG = "en";

const DICT = { en, pt, el };

// Read a dot-path from a nested object. Returns undefined when the path
// does not resolve — the caller decides how to fall back.
const readPath = (obj, path) => {
  if (!obj) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
};

// Interpolate {{name}} placeholders inside a resolved string.
const interpolate = (value, vars) => {
  if (typeof value !== "string" || !vars) return value;
  return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : `{{${key}}}`
  );
};

// Split the pathname into a language code + the remainder.
// Falls back to English for anything that doesn't start with a known code.
export const parsePath = (pathname) => {
  const match = pathname.match(/^\/(pt|el)(\/.*|$)/);
  if (match) {
    return { lang: match[1], rest: match[2] || "/" };
  }
  return { lang: DEFAULT_LANG, rest: pathname };
};

// Build a localized href. `path` is always the canonical English path
// (e.g. "/about"). The helper returns "/pt/about" or "/el/about"
// when needed and preserves "/" as the language root.
export const localizePath = (path, lang) => {
  if (!path) return "/";
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (!lang || lang === DEFAULT_LANG) return clean;
  if (clean === "/") return `/${lang}`;
  return `/${lang}${clean}`;
};

const LangContext = createContext({
  lang: DEFAULT_LANG,
  t: (key) => key,
  l: (path) => path,
});

export const LangProvider = ({ lang, children }) => {
  const value = useMemo(() => {
    const dict = DICT[lang] || DICT[DEFAULT_LANG];
    const fallback = DICT[DEFAULT_LANG];
    const t = (key, vars) => {
      const primary = readPath(dict, key);
      if (primary !== undefined) return interpolate(primary, vars);
      const fb = readPath(fallback, key);
      if (fb !== undefined) return interpolate(fb, vars);
      // Return the key itself as a last resort so missing keys are visible
      // during development — never breaks the UI.
      return key;
    };
    const l = (path) => localizePath(path, lang);
    return { lang, t, l };
  }, [lang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
export const useT = () => useContext(LangContext).t;
export const useL = () => useContext(LangContext).l;

// Helper for the LanguageSwitcher — returns the same pathname
// re-prefixed with a different language, preserving the trailing route.
export const swapLangInPath = (pathname, targetLang) => {
  const { rest } = parsePath(pathname);
  return localizePath(rest, targetLang);
};

// Suggests switching language based on the visitor's own browser
// language settings — purely a dismissible nudge, never a redirect.
// Only the diagnostics show this (see `DiagnosticLayout`), at the
// intro step only. Returns the matching `LANGS` entry, or null when
// the visitor's own top browser-language preference already matches
// the current site language, or matches no supported language at all.
export const detectBrowserLangSuggestion = (currentLang) => {
  if (typeof navigator === "undefined") return null;
  const candidates = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (const raw of candidates) {
    if (!raw) continue;
    const code = raw.slice(0, 2).toLowerCase();
    if (code === currentLang) return null;
    const match = LANGS.find((l) => l.code === code);
    if (match) return match;
  }
  return null;
};

// Route wrapper: reads the URL and mounts LangProvider with the correct code.
export const LangRouter = ({ children }) => {
  const { pathname } = useLocation();
  const { lang } = parsePath(pathname);
  return <LangProvider lang={lang}>{children}</LangProvider>;
};

export default LangProvider;
